import React, { useCallback, useState } from 'react';
import { Box, Flex, Text, Image, IconButton, Badge } from '@chakra-ui/react';
import { MdDragIndicator, MdClose } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/** A generic reorderable row: a playlist (or any entity) with an id, title and optional metadata. */
export interface SortableRow {
  id: string;
  title: string;
  subtitle?: string | null;
  thumbnailUrl?: string | null;
  /** When false, the row is visually flagged as unavailable (unpublished/deleted member). */
  isAvailable?: boolean;
}

interface RowProps {
  row: SortableRow;
  onRemove?: (id: string) => void;
  isOverlay?: boolean;
  testIdPrefix: string;
}

function Row({ row, onRemove, isOverlay = false, testIdPrefix }: RowProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const unavailable = row.isAvailable === false;

  return (
    <Flex
      ref={setNodeRef}
      style={style}
      align="center"
      gap={3}
      px={3}
      py={2}
      mb={2}
      borderRadius="12px"
      border="1px solid"
      borderColor={unavailable ? 'orange.200' : 'gray.200'}
      bg={unavailable ? 'orange.50' : 'white'}
      boxShadow={isOverlay ? '0 4px 12px rgba(0,0,0,0.15)' : undefined}
      data-testid={`${testIdPrefix}-row-${row.id}`}
    >
      <Box
        as="span"
        cursor="grab"
        color="gray.400"
        {...attributes}
        {...listeners}
        data-testid={`${testIdPrefix}-drag-${row.id}`}
        aria-label={t('admin.playlistCollections.dragHandle', 'Drag to reorder')}
      >
        <MdDragIndicator size={20} />
      </Box>

      <Box w="40px" h="40px" flexShrink={0} borderRadius="8px" overflow="hidden" bg="gray.100">
        {row.thumbnailUrl && (
          <Image src={row.thumbnailUrl} alt={row.title} w="100%" h="100%" objectFit="cover" />
        )}
      </Box>

      <Box flex={1} minW={0}>
        <Flex align="center" gap={2}>
          <Text fontWeight="500" fontSize="sm" noOfLines={1}>{row.title}</Text>
          {unavailable && (
            <Badge colorScheme="orange" fontSize="2xs" data-testid={`${testIdPrefix}-unavailable-${row.id}`}>
              {t('admin.playlistCollections.unavailable', 'Unavailable')}
            </Badge>
          )}
        </Flex>
        {row.subtitle && (
          <Text fontSize="xs" color="gray.500" noOfLines={1}>{row.subtitle}</Text>
        )}
      </Box>

      {onRemove && (
        <IconButton
          size="sm"
          variant="ghost"
          colorScheme="red"
          aria-label={t('common.remove', 'Remove')}
          icon={<MdClose />}
          onClick={() => onRemove(row.id)}
          data-testid={`${testIdPrefix}-remove-${row.id}`}
        />
      )}
    </Flex>
  );
}

interface SortablePlaylistListProps {
  rows: SortableRow[];
  /** Called with the full reordered id list after a drag ends. */
  onReorder: (orderedIds: string[]) => void;
  /** Optional remove handler; when omitted, rows are reorder-only. */
  onRemove?: (id: string) => void;
  /** Message when the list is empty. */
  emptyLabel?: string;
  /** Prefix for data-testids so multiple lists on one page don't collide. */
  testIdPrefix?: string;
}

/**
 * A generic dnd-kit sortable list of playlist rows. Reuses the same reorder mechanics
 * as DraggableTrackList (PointerSensor + arrayMove + full-list callback) so the optimistic
 * reorder mutation pattern carries over. Flags unavailable members.
 */
export function SortablePlaylistList({
  rows,
  onReorder,
  onRemove,
  emptyLabel,
  testIdPrefix = 'sortable',
}: SortablePlaylistListProps) {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = rows.findIndex((r) => r.id === active.id);
    const newIndex = rows.findIndex((r) => r.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(rows, oldIndex, newIndex);
    onReorder(reordered.map((r) => r.id));
  }, [rows, onReorder]);

  const handleDragCancel = useCallback(() => setActiveId(null), []);

  const activeRow = activeId ? rows.find((r) => r.id === activeId) : null;

  if (rows.length === 0) {
    return (
      <Box p={6} textAlign="center" data-testid={`${testIdPrefix}-empty`}>
        <Text color="gray.500" fontSize="sm">{emptyLabel ?? t('admin.playlistCollections.emptyList', 'Nothing here yet')}</Text>
      </Box>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={rows.map((r) => r.id)} strategy={verticalListSortingStrategy}>
        <Box data-testid={`${testIdPrefix}-list`}>
          {rows.map((row) => (
            <Row key={row.id} row={row} onRemove={onRemove} testIdPrefix={testIdPrefix} />
          ))}
        </Box>
      </SortableContext>
      <DragOverlay>
        {activeRow ? <Row row={activeRow} isOverlay testIdPrefix={testIdPrefix} /> : null}
      </DragOverlay>
    </DndContext>
  );
}
